import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Compass, Lightbulb, FileText, Megaphone, CalendarDays, Package, Mail,
  Target, Users, ArrowRight, ArrowLeft, Info, Shirt, LayoutTemplate, BookOpen, Handshake, Wand2,
} from "lucide-react";

// Fixed, high-quality sample journeys so beginners see exactly what a finished
// journey looks like — instant, free, identical every time (no AI call).
// They pick the example closest to what they want to sell.
const SAMPLES = [
  {
    id: "pod",
    label: "A print-on-demand product",
    tagline: "Sell t-shirts & merch with zero design skills",
    icon: Shirt,
    data: {
      goalLabel: "Sell a digital product",
      goalId: "sell_product",
      audience:
        "Complete beginners who want a low-cost side income and are curious about print-on-demand, but feel overwhelmed by where to start and worried they can't design.",
      product: "Print-on-Demand Payday — a beginner mini-course that takes you from zero to your first t-shirt sale in 14 days, no design skills needed.",
      opportunity: {
        headline: "Turn zero design skills into POD income",
        message:
          "Your audience wants a side income but is scared of the tech and the design. That fear is exactly your opening: a hand-holding, 14-day mini-course removes every excuse and gets them to a real sale fast. Low-cost, high-trust, and endlessly repeatable.",
        selling: "A 14-day beginner mini-course: 'Print-on-Demand Payday'.",
      },
      hook: {
        hook: "I made my first $1,000 with print-on-demand without designing a single thing myself — here's the exact 14-day system I'd use if I started over today.",
        alternatives: [
          "Everyone told me you need to be a designer to sell t-shirts online. I didn't own Photoshop. I still made my first sale in 11 days.",
          "If you can type a sentence, you can start a print-on-demand store this weekend. Let me prove it.",
        ],
      },
      script: {
        opening: "I made my first $1,000 with print-on-demand — and I can't design to save my life.",
        middle:
          "Here's the truth nobody tells beginners: the winning stores aren't the prettiest, they're the ones that solve a tiny, specific problem for a passionate niche. In my mini-course I show you how to find that niche in an afternoon, get done-for-you designs for free, and list your first product in under 30 minutes.",
        payoff: "By day 14 you'll have a live store, three products, and your first ad running — even if today you have zero followers and zero experience.",
        cta: "Comment 'PAYDAY' and I'll send you the free starter checklist from the course.",
      },
      pitch_test: {
        headline: "Your offer holds up — here's how to answer the doubts",
        pitch:
          "\"Print-on-Demand Payday\" is the shortcut I wish I had: 14 short lessons, a niche-finder, free design sources, and a launch checklist — so a total beginner gets a live store and their first sale in two weeks.",
        objections: [
          { objection: "\"I'm not creative / I can't design.\"", response: "The course uses free tools and done-for-you templates — you pick words and a niche, not fonts. Design skill is not required." },
          { objection: "\"Isn't print-on-demand saturated?\"", response: "Broad niches are. The course shows you how to go narrow (e.g. 'gifts for night-shift nurses') where competition is tiny and buyers are loyal." },
          { objection: "\"I don't have money to spend on stock or ads.\"", response: "Print-on-demand has zero inventory — you only pay after a customer buys. You can launch for the cost of the course and a few dollars of testing." },
        ],
      },
      content_plan: {
        days: [
          { day: 1, theme: "Myth-buster", idea: "\"You don't need to be a designer\" — show 3 best-selling plain-text shirts.", format: "Talking-head reel", cta: "Follow for the 14-day plan" },
          { day: 2, theme: "Niche secrets", idea: "How to find a hungry micro-niche in 10 minutes using free tools.", format: "Screen-record tutorial", cta: "Save this for later" },
          { day: 3, theme: "Proof", idea: "Screenshot of first sale + the exact product that sold.", format: "Story + reel", cta: "Comment 'PAYDAY' for the checklist" },
          { day: 4, theme: "Behind the scenes", idea: "Design a shirt live in under 5 minutes with free templates.", format: "Time-lapse reel", cta: "Try it and tag me" },
          { day: 5, theme: "Objection", idea: "\"Isn't it saturated?\" — the narrow-niche fix.", format: "Talking-head reel", cta: "DM 'NICHE' for examples" },
          { day: 6, theme: "Quick win", idea: "3 free tools every POD beginner should bookmark.", format: "Carousel", cta: "Save + share" },
          { day: 7, theme: "Offer day", idea: "Open the mini-course with a 14-day promise + starter bonus.", format: "Talking-head + link", cta: "Grab Print-on-Demand Payday today" },
        ],
      },
      offer: {
        name: "Print-on-Demand Payday",
        promise: "Go from zero to your first print-on-demand sale in 14 days — no design skills, no inventory, no big budget.",
        pricing: "£27 launch price (Quick Start) · £47 with the Niche Vault bonus",
        pitch:
          "Print-on-Demand Payday is the beginner-proof shortcut to your first online income. In 14 bite-sized lessons you'll find a profitable micro-niche, get done-for-you designs, launch a real store, and run your first sale — following a simple daily checklist. If you can type a sentence and follow steps, you can do this.",
      },
      email: {
        subject: "Your first t-shirt sale is closer than you think 👕",
        body:
          "Hey,\n\nIf you've ever thought \"I'd love a side income but I'm not a designer\" — this is for you.\n\nPrint-on-Demand Payday walks you from a blank screen to a live store and your first sale in just 14 days. No inventory. No design skills. No huge budget.\n\nInside you'll get the niche-finder, free design sources, and the exact daily checklist I use.\n\nStart today: [your link]\n\nSee you on the inside,\nYour name",
      },
    },
  },
  {
    id: "template",
    label: "A template or toolkit",
    tagline: "Sell Notion / spreadsheet templates people copy & use",
    icon: LayoutTemplate,
    data: {
      goalLabel: "Sell a digital product",
      goalId: "sell_product",
      audience:
        "Freelancers and small-business owners who juggle projects, clients and money in messy spreadsheets and sticky notes — they want to look professional and stay organised but don't want to build a system from scratch.",
      product: "The Freelance Command Center — a plug-and-play Notion template that manages clients, projects, invoices and income in one dashboard.",
      opportunity: {
        headline: "Sell the system, not the software",
        message:
          "People don't want another blank Notion page — they want the finished system that makes them feel in control. Your template does the thinking for them: duplicate it once and their whole freelance business is organised. High perceived value, near-zero delivery cost, and endlessly re-sellable.",
        selling: "A one-click Notion template: 'The Freelance Command Center'.",
      },
      hook: {
        hook: "I ran my entire freelance business out of 7 messy spreadsheets — until I built one Notion dashboard that replaced all of them. You can copy it in 30 seconds.",
        alternatives: [
          "If your freelance 'system' is really just 40 browser tabs and a prayer, this template will feel illegal.",
          "I almost lost a £3k client because I forgot to invoice them. Never again — here's the exact dashboard that fixed it.",
        ],
      },
      script: {
        opening: "My freelance admin used to eat a full day every week. Now it takes 20 minutes — because of one template.",
        middle:
          "Most freelancers try to build their own system and give up halfway. The Freelance Command Center is already built: a client tracker, project pipeline, invoice log and income dashboard that all talk to each other. You duplicate it once, add your clients, and everything updates itself.",
        payoff: "Within an hour you'll have a single home for every client, deadline and unpaid invoice — so nothing slips and you always know what you're owed.",
        cta: "Comment 'SYSTEM' and I'll send you a free walkthrough of how it works.",
      },
      pitch_test: {
        headline: "Strong offer — here's how to handle the pushback",
        pitch:
          "The Freelance Command Center is a done-for-you Notion dashboard that replaces the spreadsheets, notes and reminders freelancers cobble together. Duplicate it, plug in your clients, and run your whole business from one screen.",
        objections: [
          { objection: "\"I've never used Notion.\"", response: "The template comes with a 5-minute setup video and pre-filled example data. If you can drag and type, you can use it — no Notion experience needed." },
          { objection: "\"I could just build this myself for free.\"", response: "You could — and most people spend 15+ hours and still quit. You're paying to skip the building and start using it today." },
          { objection: "\"Will it work for my type of freelancing?\"", response: "It's field-agnostic — designers, writers, VAs and consultants all use it. Every section is editable, so you tailor it in minutes." },
        ],
      },
      content_plan: {
        days: [
          { day: 1, theme: "Pain reveal", idea: "Screen-record of a chaotic spreadsheet setup vs the clean dashboard.", format: "Screen-record reel", cta: "Follow for the fix" },
          { day: 2, theme: "Quick win", idea: "3 Notion tricks that instantly make you look organised.", format: "Carousel", cta: "Save this" },
          { day: 3, theme: "Demo", idea: "Add a new client to the dashboard live in under 60 seconds.", format: "Talking-head + screen", cta: "Comment 'SYSTEM' for a walkthrough" },
          { day: 4, theme: "Proof", idea: "Show the income dashboard auto-totalling a month of invoices.", format: "Screen-record reel", cta: "DM 'INCOME' to see it" },
          { day: 5, theme: "Objection", idea: "\"I've never used Notion\" — show the 5-minute setup.", format: "Tutorial reel", cta: "Save for setup day" },
          { day: 6, theme: "Behind the scenes", idea: "Why I built this after nearly losing a client.", format: "Story + talking-head", cta: "Follow for part 2" },
          { day: 7, theme: "Offer day", idea: "Open the template with a launch price + bonus invoice pack.", format: "Talking-head + link", cta: "Grab the Command Center today" },
        ],
      },
      offer: {
        name: "The Freelance Command Center",
        promise: "Run your entire freelance business — clients, projects, invoices and income — from one Notion dashboard you set up in under an hour.",
        pricing: "£19 (template only) · £39 with the Invoice & Proposal Pack bonus",
        pitch:
          "The Freelance Command Center is the plug-and-play Notion system that replaces your scattered spreadsheets and reminders. Duplicate it, add your clients, and get a single dashboard that tracks projects, chases invoices and shows exactly what you've earned. Comes with a 5-minute setup video so you're running today.",
      },
      email: {
        subject: "The 7 spreadsheets I deleted this week",
        body:
          "Hey,\n\nFor years I ran my freelance business out of a mess of spreadsheets, notes and reminders. It worked… until it didn't (I once forgot to invoice a £3k client 😬).\n\nSo I built one Notion dashboard to run everything: clients, projects, invoices and income, all in one place.\n\nIt's called the Freelance Command Center. You duplicate it once, plug in your clients, and you're organised in under an hour — no Notion experience needed.\n\nGrab it here: [your link]\n\nTo staying in control,\nYour name",
      },
    },
  },
  {
    id: "ebook",
    label: "An eBook or guide",
    tagline: "Package your knowledge into a downloadable guide",
    icon: BookOpen,
    data: {
      goalLabel: "Sell a digital product",
      goalId: "sell_product",
      audience:
        "First-time home bakers who want to start selling their cakes and bakes for extra income, but feel unsure about pricing, food-safety rules and how to find their first paying customers.",
      product: "From Kitchen to Cash — a 40-page eBook that walks a home baker from their first paid order to a steady side income.",
      opportunity: {
        headline: "Turn 'you should sell these!' into real orders",
        message:
          "Your audience already bakes well — friends keep telling them to sell. What stops them is the business side: what to charge, the legal bits, and how to get customers. An eBook that removes that fear is an easy yes, cheap to deliver, and something they can act on the same night they buy.",
        selling: "A 40-page beginner eBook: 'From Kitchen to Cash'.",
      },
      hook: {
        hook: "Everyone kept telling me 'you should sell these!' — so I did. Here's the exact guide that got me from nervous baker to fully-booked in one summer.",
        alternatives: [
          "You don't need a café or a food-truck to make money baking. You need a kitchen, a phone, and the right first three steps.",
          "I was terrified of the 'legal stuff' around selling food. Turns out it took one afternoon to sort. Let me save you the panic.",
        ],
      },
      script: {
        opening: "I turned my weekend baking hobby into a booked-out side income — without a shop, a website, or a food-hygiene degree.",
        middle:
          "The thing holding most home bakers back isn't the baking — it's not knowing what to charge, how to stay legal, and where to find that first customer. My guide gives you a simple pricing formula, a plain-English checklist for the rules, and the exact posts I used to land my first 10 orders.",
        payoff: "By the end you'll have priced your menu, ticked off the legal basics, and know exactly how to get your first paying customers this month.",
        cta: "Comment 'BAKE' and I'll send you the free pricing cheat-sheet from the book.",
      },
      pitch_test: {
        headline: "Solid offer — here's how to reassure buyers",
        pitch:
          "From Kitchen to Cash is a 40-page, beginner-friendly guide that takes a hobby baker from 'I could never sell these' to their first paid orders — covering pricing, the legal basics, and finding customers, with copy-and-paste templates.",
        objections: [
          { objection: "\"I'm not a professional baker.\"", response: "You don't need to be. The book is about turning bakes you already make into orders — it's the business side, explained simply, not a baking course." },
          { objection: "\"Isn't selling food really complicated legally?\"", response: "It feels that way until someone lays it out. The guide has a plain-English checklist for registering and staying safe — most people finish it in an afternoon." },
          { objection: "\"How do I actually find customers?\"", response: "There's a whole section with the exact posts, groups and offers that landed my first 10 orders — copy them and adapt to your area." },
        ],
      },
      content_plan: {
        days: [
          { day: 1, theme: "Relatable hook", idea: "\"Everyone says you should sell these\" — show a tray of bakes.", format: "Talking-head reel", cta: "Follow for the how-to" },
          { day: 2, theme: "Myth-buster", idea: "\"You don't need a shop or a website to start.\"", format: "Text-on-screen reel", cta: "Save this" },
          { day: 3, theme: "Quick win", idea: "The simple formula for pricing a cake so you actually profit.", format: "Carousel", cta: "Comment 'BAKE' for the cheat-sheet" },
          { day: 4, theme: "Objection", idea: "The 'legal stuff' for selling food — sorted in an afternoon.", format: "Talking-head reel", cta: "Save for later" },
          { day: 5, theme: "Proof", idea: "Screenshots of first orders + a fully-booked weekend.", format: "Story + reel", cta: "DM 'ORDERS' to hear the story" },
          { day: 6, theme: "Behind the scenes", idea: "How I found my first 10 customers with zero followers.", format: "Talking-head reel", cta: "Follow for the templates" },
          { day: 7, theme: "Offer day", idea: "Launch the eBook with a bonus pricing cheat-sheet.", format: "Talking-head + link", cta: "Grab From Kitchen to Cash today" },
        ],
      },
      offer: {
        name: "From Kitchen to Cash",
        promise: "Go from hobby baker to your first paid orders this month — with pricing, the legal basics, and customer-finding all made simple.",
        pricing: "£12 (eBook) · £22 with the Pricing & Order-Form Bundle",
        pitch:
          "From Kitchen to Cash is the 40-page guide that turns 'you should sell these!' into real income. You'll get a dead-simple pricing formula, a plain-English checklist for the legal side, and the exact posts and templates that landed my first orders — all readable in an evening and doable this month, even with zero followers.",
      },
      email: {
        subject: "\"You should sell these!\" — okay, here's how 🧁",
        body:
          "Hey,\n\nHow many times have people told you your bakes are good enough to sell?\n\nThe baking was never the hard part — it's knowing what to charge, the legal bits, and where to find your first customers. That's exactly what stops most home bakers from ever starting.\n\nFrom Kitchen to Cash is a short, do-it-tonight guide that walks you through all three: a simple pricing formula, a plain-English legal checklist, and the posts I used to land my first 10 orders.\n\nGet your copy here: [your link]\n\nHappy baking (and selling),\nYour name",
      },
    },
  },
  {
    id: "coaching",
    label: "A coaching offer",
    tagline: "Sell 1:1 or group coaching / your expertise",
    icon: Handshake,
    data: {
      goalLabel: "Promote a service",
      goalId: "get_leads",
      audience:
        "Mid-career professionals who feel stuck and quietly dread Mondays — they know they want a change but feel overwhelmed by options and scared of making the wrong move.",
      product: "The Career Reset — a 6-week 1:1 coaching programme that helps stuck professionals get clear on their next move and take the first real steps toward it.",
      opportunity: {
        headline: "Sell the clarity, not the calls",
        message:
          "People aren't buying 'coaching sessions' — they're buying relief from the fog and fear of being stuck. Position your programme around the transformation (from lost to a clear, confident plan) and a premium 1:1 offer becomes an easy decision for the right person. Fewer clients, higher value, deeply rewarding.",
        selling: "A 6-week 1:1 coaching programme: 'The Career Reset'.",
      },
      hook: {
        hook: "I spent two years 'thinking about' changing careers and did nothing. Six weeks of the right questions changed everything — here's the exact process I now use with clients.",
        alternatives: [
          "If Sunday nights come with a knot in your stomach, it's not a motivation problem. It's a clarity problem — and clarity is coachable.",
          "You don't need to blow up your life to feel excited about work again. You need a plan. Let me show you how I build one with clients.",
        ],
      },
      script: {
        opening: "The scariest part of being stuck in the wrong career isn't the job — it's the fear that you'll waste another year not deciding.",
        middle:
          "Most people try to 'figure it out' alone, spiral through 100 options, and freeze. In The Career Reset we do the opposite: over 6 weeks I help you cut through the noise, get honest about what you actually want, and build a concrete, low-risk plan for your next move — with accountability so you actually act on it.",
        payoff: "By week 6 you'll have a clear direction, a step-by-step plan, and the confidence to take the first move — without gambling your income to do it.",
        cta: "DM me 'RESET' and I'll send you a free clarity exercise to try this week.",
      },
      pitch_test: {
        headline: "Premium offer — here's how to earn the yes",
        pitch:
          "The Career Reset is a 6-week 1:1 coaching programme for professionals who feel stuck. Through weekly sessions and structured exercises, you go from foggy and anxious to a clear, confident, actionable plan for your next career move.",
        objections: [
          { objection: "\"Coaching feels expensive.\"", response: "Staying stuck is the real cost — another year of dread and lost earning potential. This is a one-off investment to get unstuck in weeks, not years, with a clear plan you keep for life." },
          { objection: "\"Can't I just figure this out myself?\"", response: "You can gather information alone, but not perspective. The value is an outside voice asking the right questions and holding you accountable so you actually decide and act." },
          { objection: "\"What if I don't know what I want?\"", response: "That's exactly who this is for. The whole first half of the programme is designed to uncover it — most clients arrive foggy and that's completely normal." },
        ],
      },
      content_plan: {
        days: [
          { day: 1, theme: "Relatable hook", idea: "\"The Sunday-night knot in your stomach\" — name the feeling.", format: "Talking-head reel", cta: "Follow if this is you" },
          { day: 2, theme: "Reframe", idea: "Being stuck is a clarity problem, not a motivation problem.", format: "Text-on-screen reel", cta: "Save this" },
          { day: 3, theme: "Quick win", idea: "One journaling prompt that cuts through career fog fast.", format: "Carousel", cta: "DM 'RESET' for the full exercise" },
          { day: 4, theme: "Objection", idea: "\"Can't I just figure it out myself?\" — why perspective beats info.", format: "Talking-head reel", cta: "Comment your biggest block" },
          { day: 5, theme: "Proof", idea: "A client's before/after: from dreading Mondays to a clear plan.", format: "Story + testimonial", cta: "DM 'STORY' to hear more" },
          { day: 6, theme: "Behind the scenes", idea: "The 6-week process, mapped out simply.", format: "Talking-head + carousel", cta: "Follow for the offer" },
          { day: 7, theme: "Offer day", idea: "Open a few Career Reset spots + a free clarity call.", format: "Talking-head + link", cta: "Book your free clarity call" },
        ],
      },
      offer: {
        name: "The Career Reset",
        promise: "Go from stuck and dreading Mondays to a clear, confident plan for your next career move — in 6 weeks, without gambling your income.",
        pricing: "£1,200 for the 6-week programme · payment plan available · free clarity call to start",
        pitch:
          "The Career Reset is a 6-week 1:1 coaching programme for professionals who feel stuck but scared to move. Through weekly sessions and structured exercises, we cut through the fog, get honest about what you actually want, and build a concrete, low-risk plan — with the accountability to make sure you act on it. You leave with clarity, a plan, and confidence you keep for good.",
      },
      email: {
        subject: "The Sunday-night knot in your stomach",
        body:
          "Hey,\n\nIf a little dread creeps in every Sunday evening, I want you to know two things.\n\nFirst: it's incredibly common. Second: it's not a motivation problem — it's a clarity problem. And clarity is coachable.\n\nI spent two years 'thinking about' changing careers and doing nothing. What finally moved me wasn't more information — it was the right questions and someone holding me to my own plan.\n\nThat's exactly what The Career Reset does: a 6-week 1:1 programme that takes you from foggy and stuck to a clear, confident next move — without gambling your income.\n\nWant to talk it through? Book a free clarity call here: [your link]\n\nTo Mondays you don't dread,\nYour name",
      },
    },
  },
];

function SampleCard({ icon: Icon, title, children }) {
  return (
    <Card className="fade-up border-border p-5" data-testid={`sample-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon size={15} className="text-primary" /> {title}
      </div>
      <div className="text-sm leading-relaxed">{children}</div>
    </Card>
  );
}

function SamplePicker({ onPick, onExit }) {
  return (
    <div className="min-h-screen bg-background pb-16" data-testid="sample-picker">
      <div className="sticky top-0 z-30 flex flex-col items-center gap-2 border-b border-accent/30 bg-accent/10 px-4 py-3 text-center backdrop-blur-xl sm:flex-row sm:justify-between sm:text-left"
        data-testid="sample-picker-banner">
        <span className="flex items-center gap-2 text-sm font-semibold text-accent">
          <Info size={16} /> Pick the example closest to what you want to sell.
        </span>
        <Button onClick={onExit} size="sm" variant="outline" data-testid="sample-picker-start"
          className="rounded-full border-accent/40 text-accent hover:bg-accent/15">
          Start My Own Journey
        </Button>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Sample journeys</p>
        <h1 className="mt-2 font-head text-3xl font-bold sm:text-4xl">See a finished journey, start to finish</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Choose the one closest to what you'd sell — you'll see the full plan a beginner would get.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {SAMPLES.map((s) => {
            const Icon = s.icon;
            return (
              <button key={s.id} onClick={() => onPick(s.id)} data-testid={`sample-pick-${s.id}`}
                className="group flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-6 text-left transition-transform duration-200 hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon size={22} />
                </div>
                <div>
                  <h3 className="font-head text-lg font-bold group-hover:text-primary">{s.label}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{s.tagline}</p>
                </div>
                <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                  See the journey <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SampleDetail({ sample, onBack, onExit, onUseInputs }) {
  const s = sample.data;
  const StartBtn = ({ testid }) => (
    <Button onClick={onExit} variant="outline" data-testid={testid}
      className="rounded-full border-primary/30 px-7 py-6 text-base font-semibold text-primary hover:bg-primary/5">
      Start From Scratch <ArrowRight size={18} className="ml-1.5" />
    </Button>
  );
  const UseInputsBtn = ({ testid }) => (
    <Button onClick={() => onUseInputs(s)} data-testid={testid}
      className="rounded-full bg-primary px-7 py-6 text-base font-semibold hover:bg-primary/90">
      <Wand2 size={18} className="mr-1.5" /> Use These Inputs
    </Button>
  );
  return (
    <div className="min-h-screen bg-background pb-16" data-testid="sample-journey">
      {/* Persistent example banner */}
      <div className="sticky top-0 z-30 flex flex-col items-center gap-2 border-b border-accent/30 bg-accent/10 px-4 py-3 text-center backdrop-blur-xl sm:flex-row sm:justify-between sm:text-left"
        data-testid="sample-banner">
        <button onClick={onBack} data-testid="sample-back-to-picker"
          className="inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline">
          <ArrowLeft size={15} /> Other examples
        </button>
        <span className="flex items-center gap-2 text-sm font-semibold text-accent">
          <Info size={16} /> This is an example — build yours below.
        </span>
        <Button onClick={() => onUseInputs(s)} size="sm" data-testid="sample-start-top"
          className="rounded-full bg-primary text-white hover:bg-primary/90">
          <Wand2 size={14} className="mr-1" /> Use These Inputs
        </Button>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Sample journey · {sample.label}</p>
        <h1 className="mt-2 font-head text-3xl font-bold sm:text-4xl">Here's a full journey, start to finish</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This is exactly what your finished plan will look like.
        </p>

        <div className="mt-8 space-y-4">
          <SampleCard icon={Target} title="The goal">
            <p className="font-semibold">{s.goalLabel}</p>
          </SampleCard>
          <SampleCard icon={Users} title="The audience">
            <p>{s.audience}</p>
          </SampleCard>
          <SampleCard icon={Package} title="The product">
            <p>{s.product}</p>
          </SampleCard>
          <SampleCard icon={Compass} title="Best opportunity">
            <p className="font-semibold">{s.opportunity.headline}</p>
            <p className="mt-1 text-muted-foreground">{s.opportunity.message}</p>
          </SampleCard>
          <SampleCard icon={Lightbulb} title="Your hook">
            <p className="font-medium">{s.hook.hook}</p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Alternatives</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-muted-foreground">
              {s.hook.alternatives.map((a, i) => <li key={a || i}>{a}</li>)}
            </ul>
          </SampleCard>
          <SampleCard icon={FileText} title="Your script">
            <p><span className="font-semibold">Opening:</span> {s.script.opening}</p>
            <p className="mt-1"><span className="font-semibold">Middle:</span> {s.script.middle}</p>
            <p className="mt-1"><span className="font-semibold">Payoff:</span> {s.script.payoff}</p>
            <p className="mt-1"><span className="font-semibold">CTA:</span> {s.script.cta}</p>
          </SampleCard>
          <SampleCard icon={Megaphone} title="Pitch test">
            <p className="font-semibold">{s.pitch_test.headline}</p>
            <p className="mt-1 text-muted-foreground">{s.pitch_test.pitch}</p>
            <div className="mt-3 space-y-2">
              {s.pitch_test.objections.map((o, i) => (
                <div key={o.objection || i} className="rounded-lg bg-secondary/40 p-3">
                  <p className="font-semibold">{o.objection}</p>
                  <p className="mt-0.5 text-muted-foreground">{o.response}</p>
                </div>
              ))}
            </div>
          </SampleCard>
          <SampleCard icon={CalendarDays} title="7-day content plan">
            <ul className="space-y-1.5">
              {s.content_plan.days.map((d) => (
                <li key={d.day}>
                  <span className="font-semibold">Day {d.day} — {d.theme}:</span> {d.idea}
                  <span className="text-muted-foreground"> ({d.format})</span>
                </li>
              ))}
            </ul>
          </SampleCard>
          <SampleCard icon={Package} title="Your packaged offer">
            <p className="font-semibold">{s.offer.name}</p>
            <p className="mt-1">{s.offer.promise}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pricing</p>
            <p>{s.offer.pricing}</p>
            <p className="mt-2 text-muted-foreground">{s.offer.pitch}</p>
          </SampleCard>
          <SampleCard icon={Mail} title="Promotional email">
            <p className="font-semibold">Subject: {s.email.subject}</p>
            <p className="mt-2 whitespace-pre-line text-muted-foreground">{s.email.body}</p>
          </SampleCard>
        </div>

        <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-6 text-center">
          <p className="font-head text-lg font-bold">Ready to build yours?</p>
          <p className="text-sm text-muted-foreground">Load this example's goal and audience straight into your own journey — then tweak and continue. Free for your first run.</p>
          <div className="mt-1 flex flex-col gap-2 sm:flex-row">
            <UseInputsBtn testid="sample-use-inputs-bottom" />
            <StartBtn testid="sample-start-bottom" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function SampleJourney({ onExit, onUseInputs }) {
  const [pickedId, setPickedId] = useState(null);
  const picked = SAMPLES.find((s) => s.id === pickedId);

  if (!picked) {
    return <SamplePicker onPick={setPickedId} onExit={onExit} />;
  }
  return <SampleDetail sample={picked} onBack={() => setPickedId(null)} onExit={onExit} onUseInputs={onUseInputs} />;
}
